import React, { useState, useEffect, useMemo } from 'react';
import { getDatabase, ref, onValue, push, update, remove } from 'firebase/database';
import './StockManagement.css';

const StockManagement = () => {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '', category: '', quantity: '', location: '', condition: 'Good', 
    remarks: '', session: '', term: '', date: new Date().toISOString().split('T')[0]
  });
  const db = getDatabase();

  // Sessions from 2023/2024 to 2090/2091
  const sessions = Array.from({ length: 68 }, (_, i) => {
    const startYear = 2023 + i;
    return `${startYear}/${startYear + 1}`;
  });

  const terms = ['FIRST TERM', 'SECOND TERM', 'THIRD TERM'];
  const categories = ["Computer Department", "Store", "Chemistry Lab", "Principal Office", 
                      "V. Principal Office", "Headmistress Office", "School Building", 
                      "Office of the Bursar", "Others"];
  const locations = ["Flat 3A", "Computer facilities", "Staff Quarters"];
  const conditions = ["Good", "Faulty", "In Use", "Damaged", "New"];

  // Load stock items
  useEffect(() => {
    const stockRef = ref(db, 'stock');
    onValue(stockRef, (snapshot) => {
      const data = snapshot.val();
      const loadedItems = data ? Object.keys(data).map(key => ({ id: key, ...data[key] })) : [];
      setItems(loadedItems);
      setFilteredItems(loadedItems);
    });
  }, [db]);

  // Filter items
  useEffect(() => {
    let filtered = items;
    
    if (selectedSession) {
      filtered = filtered.filter(item => item.session === selectedSession);
    }
    
    if (selectedTerm) {
      filtered = filtered.filter(item => item.term === selectedTerm);
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(item => 
        (item.name && item.name.toLowerCase().includes(query)) ||
        (item.category && item.category.toLowerCase().includes(query)) ||
        (item.location && item.location.toLowerCase().includes(query))
      );
    }
    
    setFilteredItems(filtered);
  }, [selectedSession, selectedTerm, searchQuery, items]);

  // Generate search suggestions
  const searchSuggestions = useMemo(() => {
    if (!searchQuery.trim() || !items.length) return [];
    
    const query = searchQuery.toLowerCase().trim();
    const suggestions = new Set();
    
    items.forEach(item => {
      if (item.name && item.name.toLowerCase().includes(query)) suggestions.add(item.name);
      if (item.category && item.category.toLowerCase().includes(query)) suggestions.add(item.category);
      if (item.location && item.location.toLowerCase().includes(query)) suggestions.add(item.location);
    });
    
    return Array.from(suggestions).slice(0, 5);
  }, [searchQuery, items]);

  // Handle search
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setShowSuggestions(false);
  };

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        ...item,
        quantity: item.quantity || '',
        session: item.session || sessions[0],
        term: item.term || terms[0]
      });
    } else {
      setEditingItem(null);
      setFormData({
        name: '', category: '', quantity: '', location: '', condition: 'Good', 
        remarks: '', session: sessions[0], term: terms[0], 
        date: new Date().toISOString().split('T')[0]
      });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert("Please provide item name.");
      return;
    }
    
    if (!formData.category) {
      alert("Please select a category.");
      return;
    }

    try {
      const stockItem = {
        name: formData.name.trim(),
        category: formData.category,
        quantity: formData.quantity ? parseInt(formData.quantity) : 0,
        location: formData.location || 'Not specified',
        condition: formData.condition,
        remarks: formData.remarks || '',
        session: formData.session || sessions[0],
        term: formData.term || terms[0],
        date: formData.date,
        lastUpdated: new Date().toISOString()
      };

      if (editingItem) {
        await update(ref(db, `stock/${editingItem.id}`), stockItem);
        alert('Item updated successfully!');
      } else {
        await push(ref(db, 'stock'), stockItem);
        alert('Item added successfully!');
      }
      
      closeModal();
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await remove(ref(db, `stock/${id}`));
        alert('Item deleted successfully!');
      } catch (error) {
        alert(`Error: ${error.message}`);
      }
    }
  };

  const clearAllFilters = () => {
    setSelectedSession('');
    setSelectedTerm('');
    setSearchQuery('');
    setShowSuggestions(false);
  };

  // Stats calculation
  const totalItems = filteredItems.length;
  const goodConditionItems = items.filter(item => item.condition === 'Good').length;
  const totalCategories = new Set(items.map(item => item.category)).size;

  return (
    <div className="stock-management-container">
      <h2>📦 Stock Management</h2>

      <div className="filter-section">
        <div className="filter-group">
          <label>🔍 Search:</label>
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by name, category..."
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => setShowSuggestions(true)}
            />
            {searchQuery && (
              <button className="clear-search" onClick={handleClearSearch}>✕</button>
            )}
            {showSuggestions && searchSuggestions.length > 0 && (
              <div className="suggestions-dropdown">
                {searchSuggestions.map((suggestion, index) => (
                  <div key={index} className="suggestion-item" onClick={() => handleSuggestionClick(suggestion)}>
                    🔍 {suggestion}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="filter-group">
          <label>📅 Session:</label>
          <select value={selectedSession} onChange={(e) => setSelectedSession(e.target.value)}>
            <option value="">All Sessions</option>
            {sessions.map(session => (
              <option key={session} value={session}>{session}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>📚 Term:</label>
          <select value={selectedTerm} onChange={(e) => setSelectedTerm(e.target.value)}>
            <option value="">All Terms</option>
            {terms.map(term => (
              <option key={term} value={term}>{term}</option>
            ))}
          </select>
        </div>

        <button className="btn btn-primary" onClick={() => openModal()}>
          ➕ Add Item
        </button>
      </div>

      <div className="summary-stats">
        <div className="stat-card">
          <div className="stat-number">{totalItems}</div>
          <div className="stat-label">Total Items</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{goodConditionItems}</div>
          <div className="stat-label">Good Condition</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{totalCategories}</div>
          <div className="stat-label">Categories</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {selectedSession || selectedTerm || searchQuery ? 'Filtered' : 'All'}
          </div>
          <div className="stat-label">View</div>
        </div>
      </div>

      {modalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{editingItem ? '✏️ Edit Item' : '➕ Add New Item'}</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} />
              </div>
              
              <div className="form-group">
                <label>Category *</label>
                <select name="category" value={formData.category} onChange={handleInputChange}>
                  <option value="">Select Category</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Session *</label>
                <select name="session" value={formData.session} onChange={handleInputChange}>
                  {sessions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Term *</label>
                <select name="term" value={formData.term} onChange={handleInputChange}>
                  {terms.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Quantity</label>
                <input type="number" name="quantity" value={formData.quantity} onChange={handleInputChange} />
              </div>

              <div className="form-group">
                <label>Location</label>
                <select name="location" value={formData.location} onChange={handleInputChange}>
                  <option value="">Select Location</option>
                  {locations.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Condition</label>
                <select name="condition" value={formData.condition} onChange={handleInputChange}>
                  {conditions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="form-group full-width">
                <label>Remarks</label>
                <input type="text" name="remarks" value={formData.remarks} onChange={handleInputChange} />
              </div>

              <div className="form-group">
                <label>Date</label>
                <input type="date" name="date" value={formData.date} onChange={handleInputChange} />
              </div>
            </div>

            <div className="modal-buttons">
              <button className="btn btn-success" onClick={handleSave}>
                {editingItem ? '💾 Update' : '💾 Save'}
              </button>
              <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="stock-list-section">
        <div className="table-header">
          <h3>📋 Stock Items ({totalItems})</h3>
          {(selectedSession || selectedTerm || searchQuery) && (
            <button className="btn-clear-filter" onClick={clearAllFilters}>
              Clear Filters
            </button>
          )}
        </div>
        
        {filteredItems.length === 0 ? (
          <div className="no-results">
            <div className="no-results-icon">📭</div>
            <h4>No items found</h4>
            <p>
              {(searchQuery || selectedSession || selectedTerm) 
                ? 'Try changing your filters.' 
                : 'Click "Add Item" to add your first item.'}
            </p>
            {(searchQuery || selectedSession || selectedTerm) && (
              <button className="btn btn-secondary" onClick={clearAllFilters}>
                Clear All Filters
              </button>
            )}
          </div>
        ) : (
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>S/N</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Qty</th>
                  <th>Location</th>
                  <th>Condition</th>
                  <th>Session</th>
                  <th>Term</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item, index) => (
                  <tr key={item.id}>
                    <td>{index + 1}</td>
                    <td><strong>{item.name}</strong></td>
                    <td>{item.category}</td>
                    <td>
                      <span className={`quantity-badge ${item.quantity > 10 ? 'high' : item.quantity > 5 ? 'medium' : 'low'}`}>
                        {item.quantity || 0}
                      </span>
                    </td>
                    <td>{item.location || '-'}</td>
                    <td>
                      <span className={`condition-badge ${item.condition?.toLowerCase().replace(' ', '-')}`}>
                        {item.condition}
                      </span>
                    </td>
                    <td>{item.session}</td>
                    <td>{item.term}</td>
                    <td className="actions">
                      <button className="btn-edit" onClick={() => openModal(item)}>✏️ Edit</button>
                      <button className="btn-delete" onClick={() => handleDelete(item.id)}>🗑️ Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockManagement;